import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, CustomConnectorDto } from '@/models/connectors';
import { usePrepareConnectorFlowQuery } from '@/services/endpoints/workspace-connectors';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CustomConnectorFlowStepProps = {
  audience: BaseAudience;
  connector: CustomConnectorDto;
  flow: ConnectorFlowDTO;
  onNext: (params: Record<string, any>, mappings: Record<string, any>) => void;
  onCancel: () => void;
};

export const CustomConnectorFlowStep: FC<CustomConnectorFlowStepProps> = ({ audience, connector, flow, onNext, onCancel }) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const { data: preparedFlow, isLoading: isPreparingFlow } = usePrepareConnectorFlowQuery({ workspaceId, id: flow.id });

  const [formData, setFormData] = useState({});
  const [mappingData, setMappingData] = useState({});

  const flowConfig = preparedFlow?.configuration?.['configurations']?.[0]?.elements;
  const paramsConfig = flowConfig?.params?.reactJsonSchemaForm;
  const mappingsConfig = flowConfig?.mappings?.reactJsonSchemaForm;

  const handleSubmit = () => {
    onNext(formData, mappingData);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel',
    onClick: onCancel,
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON:confirm',
    onClick: handleSubmit,
    disabled: !formData || Object.keys(formData).length === 0,
  };

  return (
    <div className='flex grow flex-col gap-6 p-6'>
      <div className='flex flex-col border-b border-secondary-border pb-8'>
        <h2 className='text-lg font-semibold leading-7 text-primary-900'>{t('ALL:destinations.steps.selectProperties.title')}</h2>
        <h4 className='text-sm leading-5 text-tertiary-600'>
          {t('ALL:destinations.steps.configureFlow.description', { audienceName: audience.name, connectorName: connector.name })}
        </h4>
      </div>

      <Loadable isLoading={isPreparingFlow}>
        <div className='space-y-8'>
          {paramsConfig && paramsConfig.schema && paramsConfig.uiSchema && (
            <div>
              <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.flowParameters')}</h3>
              <Form
                schema={paramsConfig.schema}
                uiSchema={paramsConfig.uiSchema}
                formData={formData}
                onChange={({ formData }) => setFormData(formData)}
                validator={validator}
              >
                <Fragment />
              </Form>
            </div>
          )}

          {mappingsConfig && Object.keys(mappingsConfig.schema.properties) > 0 && (
            <div>
              <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
              <Form
                schema={mappingsConfig.schema}
                uiSchema={mappingsConfig.uiSchema}
                formData={mappingData}
                onChange={({ formData }) => setMappingData(formData)}
                validator={validator}
              >
                <Fragment />
              </Form>
            </div>
          )}
        </div>
      </Loadable>

      <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
    </div>
  );
};
